/**
 * DocumentSignatureGrid Component
 * Single Responsibility: Display grid of documents with signature status
 */

import React from 'react'
import { ComplianceDocument } from '../../../types/meeting'
import { CheckCircle, Clock, AlertCircle, FileText, Mail, Edit3 } from 'lucide-react'

interface DocumentSignatureGridProps {
  documents: ComplianceDocument[]
  onRequestSignature: (doc: ComplianceDocument, method: 'digital' | 'email' | 'paper') => void
  onPreviewDocument: (doc: ComplianceDocument) => void
}

const DocumentSignatureGrid: React.FC<DocumentSignatureGridProps> = ({
  documents,
  onRequestSignature,
  onPreviewDocument
}) => {
  const getStatusIcon = (status: ComplianceDocument['signatureStatus']) => {
    switch (status) {
      case 'signed':
        return <CheckCircle className="w-5 h-5 text-green-500" />
      case 'pending':
        return <Clock className="w-5 h-5 text-yellow-500" />
      case 'unsigned':
        return <AlertCircle className="w-5 h-5 text-red-500" />
      case 'paper':
        return <FileText className="w-5 h-5 text-blue-500" />
    }
  }

  const getStatusColor = (status: ComplianceDocument['signatureStatus']) => {
    switch (status) {
      case 'signed':
        return 'border-green-200 bg-green-50'
      case 'pending':
        return 'border-yellow-200 bg-yellow-50'
      case 'unsigned':
        return 'border-red-200 bg-red-50'
      case 'paper':
        return 'border-blue-200 bg-blue-50'
    }
  }

  const getStatusText = (doc: ComplianceDocument) => {
    switch (doc.signatureStatus) {
      case 'signed':
        return `Signed ${doc.signedAt ? new Date(doc.signedAt).toLocaleString() : ''}`
      case 'pending':
        return 'Signature requested - awaiting response'
      case 'unsigned':
        return 'Requires signature'
      case 'paper':
        return `Paper signature verified ${doc.signedAt ? new Date(doc.signedAt).toLocaleString() : ''}`
    }
  }

  if (documents.length === 0) {
    return (
      <div className="text-center py-8 text-slate-500">
        No documents requiring review
      </div>
    )
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      {documents.map(doc => (
        <div
          key={doc.id}
          className={`border-2 rounded-lg p-4 transition-all ${getStatusColor(doc.signatureStatus)}`}
        >
          {/* Header */}
          <div className="flex items-start justify-between mb-3">
            <div className="flex items-start gap-3">
              {getStatusIcon(doc.signatureStatus)}
              <div className="flex-1">
                <h4 className="font-semibold text-slate-800">{doc.title}</h4>
                <p className="text-sm text-slate-600 mt-1">
                  {getStatusText(doc)}
                </p>
              </div>
            </div>
          </div>

          {/* Document Type Badge */}
          <div className="mb-3">
            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-slate-100 text-slate-800">
              {doc.documentType === 'disclosure' && '📋 Disclosure'}
              {doc.documentType === 'agreement' && '📄 Agreement'}
              {doc.documentType === 'form' && '📝 Form'}
            </span>
          </div>

          {/* Disclosures (if any) */}
          {doc.requiredDisclosures && doc.requiredDisclosures.length > 0 && (
            <div className="mb-3 p-2 bg-white rounded border border-slate-200">
              <p className="text-xs font-medium text-slate-600 mb-1">Required Disclosures:</p>
              <div className="space-y-1">
                {doc.requiredDisclosures.map(disclosure => (
                  <div key={disclosure.id} className="flex items-center gap-2 text-xs">
                    <input
                      type="checkbox"
                      checked={disclosure.completed}
                      disabled
                      className="w-3 h-3"
                    />
                    <span className={disclosure.completed ? 'text-green-600' : 'text-slate-600'}>
                      {disclosure.label}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Description */}
          {doc.description && (
            <p className="text-sm text-slate-600 mb-3">{doc.description}</p>
          )}

          {/* Action Buttons */}
          <div className="flex gap-2 mt-3">
            <button
              onClick={() => onPreviewDocument(doc)}
              className="px-3 py-1.5 bg-white text-slate-600 border border-slate-300 rounded text-sm hover:bg-slate-50 transition-all"
            >
              👁 Preview
            </button>

            {doc.signatureStatus === 'unsigned' && (
              <>
                <button
                  onClick={() => onRequestSignature(doc, 'digital')}
                  className="px-3 py-1.5 bg-blue-500 text-white rounded text-sm hover:bg-blue-600 transition-all"
                >
                  ✍️ Sign Now
                </button>
                <button
                  onClick={() => onRequestSignature(doc, 'email')}
                  className="px-3 py-1.5 bg-slate-500 text-white rounded text-sm hover:bg-slate-600 transition-all"
                >
                  <Mail className="w-3 h-3 inline mr-1" />
                  Email
                </button>
                <button
                  onClick={() => onRequestSignature(doc, 'paper')}
                  className="px-3 py-1.5 bg-white text-slate-600 border border-slate-300 rounded text-sm hover:bg-slate-50 transition-all"
                >
                  <Edit3 className="w-3 h-3 inline mr-1" />
                  Manual
                </button>
              </>
            )}

            {doc.signatureStatus === 'pending' && (
              <span className="px-3 py-1.5 bg-yellow-100 text-yellow-700 rounded text-sm">
                ⏳ Awaiting signature...
              </span>
            )}

            {doc.signatureStatus === 'signed' && doc.signedBy && (
              <span className="px-3 py-1.5 bg-green-100 text-green-700 rounded text-sm">
                ✅ Signed by {doc.signedBy}
              </span>
            )}
          </div>
        </div>
      ))}
    </div>
  )
}

export default DocumentSignatureGrid